import React from "react";
import { Link } from "react-router-dom";

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-6 text-center text-gray-900">
          Terms of Use
        </h1>

        {/* About SatStacker */}
        <section className="space-y-3 mb-8 text-gray-800">
          <p>
            SatStacker is a planning, automation, and tracking app for recurring
            Bitcoin purchases (“DCA”). SatStacker does <strong>not</strong> buy,
            sell, exchange, transmit, or custody cryptocurrency or fiat funds.
            SatStacker is not a cryptocurrency exchange, broker, money transmitter,
            wallet provider, custodian, or financial institution.
          </p>
          <p>
            To make any real Bitcoin purchases, sales, transfers, or other
            provider-supported transactions, you must use your own account with a
            supported third-party provider. All transactions, custody, exchange,
            transfer, settlement, KYC/AML, eligibility, limits, and compliance
            controls occur directly between you and the applicable third-party
            provider through your own provider account.
          </p>
          <p>
            By using SatStacker, you agree to these Terms of Use and our{" "}
            <Link to="/privacy" className="text-blue-600 underline">
              Privacy Policy
            </Link>
            .
          </p>
        </section>

        {/* Subscription (Auto-Renewing) */}
        <section className="space-y-3 mb-8">
          <h2 className="text-xl font-semibold text-gray-900">
            Subscription: Smart Timing Plan (Auto-Renewing)
          </h2>
          <ul className="list-disc list-inside text-gray-800 space-y-2">
            <li>
              <strong>Title:</strong> Smart Timing Plan
            </li>
            <li>
              <strong>Length:</strong> 1-month auto-renewing subscription
            </li>
            <li>
              <strong>Price:</strong> The monthly price is shown on the purchase
              screen in your app store (Apple App Store or Google Play Store) at
              checkout and may vary by region. Taxes may apply.
            </li>
            <li>
              <strong>Auto-renewal:</strong> Subscriptions renew automatically
              unless canceled at least 24 hours before the end of the current
              period.
            </li>
            <li>
              <strong>Billing:</strong> Payment is charged to your{" "}
              <strong>app store account</strong> (Apple ID or Google Play
              account) at confirmation of purchase. Your account will be charged
              for renewal within 24 hours prior to the end of the current
              period.
            </li>
            <li>
              <strong>Manage / Cancel:</strong> You can manage or cancel your
              subscription at any time in your app store account settings.
            </li>
            <li>
              <strong>Access Provided:</strong> Smart Timing features that help
              automate and schedule DCA rules (e.g., dip-aware allocation),
              plus related in-app enhancements. No performance is guaranteed.
            </li>
          </ul>
        </section>

        {/* Payments & Fees Disclosure */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Payments & Fees
          </h2>
          <p>
            Subscription payments are processed securely by your app store
            provider. SatStacker subscription or software fees are separate from
            any third-party provider transaction, exchange, payment, or network
            fees.
          </p>
          <p>
            When you use a supported third-party provider to create or execute a
            buy plan or other provider-supported transaction, any applicable
            provider fees, third-party processing fees, or Bitcoin network fees
            are shown by the applicable provider. SatStacker does not process,
            settle, custody, or convert fiat or Bitcoin.
          </p>
        </section>

        {/* Third-Party Services & Responsibility */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Third-Party Services
          </h2>
          <p>
            Supported third-party providers are independent services with their
            own terms, privacy policies, fees, limits, eligibility requirements,
            compliance procedures, and geographic availability.
          </p>
          <p>
            The applicable third-party provider determines whether you are
            eligible to use its services, which features are available to you,
            which payment methods are supported, and whether any transaction is
            approved, rejected, delayed, limited, or reversed.
          </p>
          <p>
            SatStacker availability in a country or region does not guarantee
            that any particular third-party provider, payment method, Bitcoin
            purchase, Bitcoin sale, transfer, or app feature is available to
            every user in that country or region. Your use of any third-party
            provider is governed by that provider’s terms, and you are
            responsible for ensuring your use complies with applicable laws and
            provider policies.
          </p>
        </section>

        {/* Third-Party Platform Use */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Third-Party Platform Use
          </h2>
          <p>
            SatStacker tools, algorithms, or related functionality may also be
            made available through approved third-party platforms. If you access
            SatStacker-powered functionality through a third-party platform, your
            use of that platform and any transactions conducted through that
            platform are governed by the platform’s own terms, privacy policy,
            compliance procedures, eligibility requirements, and fee disclosures.
          </p>
          <p>
            SatStacker does not become the provider of exchange, custody,
            brokerage, money transmission, payment, wallet, or financial services
            simply because SatStacker tools or algorithms are made available
            through a third-party platform.
          </p>
        </section>

        {/* Geographic Availability */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            Geographic Availability
          </h2>
          <p>
            SatStacker may not be available in all countries or regions. We may
            add, remove, restrict, or disable availability or features at any
            time based on third-party provider availability, legal requirements,
            app store requirements, operational considerations, or compliance
            reasons.
          </p>
          <p>
            If a supported third-party provider is unavailable in your country or
            region, if your provider account is not eligible, or if a provider
            feature is not supported for you, SatStacker features that depend on
            that provider may not work.
          </p>
        </section>

        {/* No Custody / No Advice */}
        <section className="space-y-3 mb-8 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">
            No Custody, No Exchange & No Advice
          </h2>
          <p>
            SatStacker does not custody funds or digital assets and does not
            provide cryptocurrency exchange, brokerage, money transmission,
            payment, wallet, or financial services. SatStacker provides software
            tools that help you plan, automate, and track activity through your
            own account with a supported third-party provider or through an
            approved third-party platform.
          </p>
          <p>
            SatStacker does not provide investment, tax, financial, or legal
            advice. Bitcoin prices are volatile; past performance does not
            guarantee future results.
          </p>
          <p>
            SatStacker-powered timing, automation, allocation, or smart-buy
            features are software tools only. They do not guarantee better
            pricing, investment performance, execution, savings, returns, or
            profit. You remain responsible for deciding whether to use any plan,
            rule, automation, or transaction.
          </p>
        </section>

        {/* Support + Legal */}
        <section className="space-y-3 text-gray-800">
          <h2 className="text-xl font-semibold text-gray-900">Support</h2>
          <p>
            For assistance, contact{" "}
            <a
              href="mailto:support@satstacker.app"
              className="text-blue-600 underline"
            >
              support@satstacker.app
            </a>
            . Please also review our{" "}
            <Link to="/privacy" className="text-blue-600 underline">
              Privacy Policy
            </Link>
            .
          </p>
          <p className="text-sm text-gray-600">
            Effective date: {new Date().toLocaleDateString()}
          </p>
        </section>
      </div>
    </div>
  );
}